import DOMUtils from '../utils/DOMUtils.js';

/**
 * CharacterShadow - Projects circular shadow straight down from character onto surfaces
 *
 * Projects shadow vertically downward onto all intersected faces:
 * - Top faces: circular gradient that fades with distance
 * - Side faces: vertical streak gradients on walls the shadow cylinder intersects
 * Uses overlay divs on cuboid face elements for automatic depth ordering.
 */
export default class CharacterShadow {
  // Default shadow parameters
  static DEFAULT_RADIUS = 0.5;       // Half of footprint (1x1 base)
  static DEFAULT_MAX_DISTANCE = 3;   // Max shadow distance (3 cuboid heights)
  static DEFAULT_MAX_OPACITY = 0.4;  // Opacity at feet
  static POSITION_THRESHOLD = 0.01;  // Re-raycast if moved > this amount

  /**
   * Create a new character shadow
   * @param {Character} character - The character to cast shadow from
   * @param {CharacterCollisionSystem} collisionSystem - For spatial queries
   */
  constructor(character, collisionSystem) {
    this.character = character;
    this.collisionSystem = collisionSystem;

    // Tunable shadow parameters (can be modified via debug UI)
    this.radius = CharacterShadow.DEFAULT_RADIUS;
    this.maxDistance = CharacterShadow.DEFAULT_MAX_DISTANCE;
    this.maxOpacity = CharacterShadow.DEFAULT_MAX_OPACITY;

    // Map of "cuboidKey:face" -> overlay element
    this._shadowedFaces = new Map();

    // Cache for change detection
    this._lastPosition = null;
    this._lastZoom = null;
    this._lastCameraAngle = null;
    this._lastParams = null;

    // Currently active shadow face keys (for cleanup)
    this._activeFaceKeys = new Set();
  }

  /**
   * Update shadow projection
   * @param {Object} camera - Camera object
   */
  update(camera) {
    // Check if update needed
    if (!this._needsUpdate(camera)) {
      return;
    }

    // Store current state for change detection
    this._lastPosition = {
      x: this.character.x,
      y: this.character.y,
      z: this.character.z
    };
    this._lastZoom = camera.zoomLevel || 1;
    this._lastCameraAngle = camera.angle || 0;
    this._lastParams = `${this.radius},${this.maxDistance},${this.maxOpacity}`;

    this._applyAll(camera);
  }

  /**
   * Force re-application of shadow overlays after KD tree fragment rebuild.
   * Called by the render pipeline after sortAndRenderCuboids() creates new fragments,
   * because the initial update() during updateCharacters() targets stale fragment
   * objects that get destroyed and rebuilt during the sort phase.
   * @param {Object} camera - Camera object
   */
  reapplyForFragments(camera) {
    this._applyAll(camera);
  }

  /**
   * Core shadow application logic shared by update() and reapplyForFragments().
   * @param {Object} camera - Camera object
   */
  _applyAll(camera) {
    // Find all faces that should receive shadow (top faces + side faces)
    const faceHits = this._findAllShadowFaces();

    // Redirect hits to KD tree fragment faces when the original cuboid is split.
    // When a cuboid is KD-split for depth sorting (e.g., character overlapping it),
    // the original is hidden and fragments are shown. Shadow overlays must target
    // the visible fragment faces, not the hidden original's faces.
    const resolvedHits = this._resolveKDFragments(faceHits);

    // Track which faces are active this frame
    const newActiveFaceKeys = new Set();

    // Apply shadow to each hit face
    for (const hit of resolvedHits) {
      const faceKey = `${hit.cuboid.getKey()}:${hit.face}`;
      newActiveFaceKeys.add(faceKey);
      this._applyShadowToFace(hit, camera);
    }

    // Hide shadows on faces that are no longer hit
    for (const key of this._activeFaceKeys) {
      if (!newActiveFaceKeys.has(key)) {
        const overlay = this._shadowedFaces.get(key);
        if (overlay) {
          overlay.style.display = 'none';
        }
      }
    }

    this._activeFaceKeys = newActiveFaceKeys;
  }

  /**
   * Check if shadow needs recalculation
   * @param {Object} camera - Camera object
   * @returns {boolean} True if update needed
   */
  _needsUpdate(camera) {
    if (!this._lastPosition) return true;

    // Check zoom changes
    const zoom = camera.zoomLevel || 1;
    if (this._lastZoom !== zoom) return true;

    // Check camera angle changes (face visibility changes when camera rotates)
    const cameraAngle = camera.angle || 0;
    if (this._lastCameraAngle !== cameraAngle) return true;

    // Check parameter changes
    const paramsKey = `${this.radius},${this.maxDistance},${this.maxOpacity}`;
    if (this._lastParams !== paramsKey) return true;

    const dx = Math.abs(this.character.x - this._lastPosition.x);
    const dy = Math.abs(this.character.y - this._lastPosition.y);
    const dz = Math.abs(this.character.z - this._lastPosition.z);

    return dx > CharacterShadow.POSITION_THRESHOLD ||
           dy > CharacterShadow.POSITION_THRESHOLD ||
           dz > CharacterShadow.POSITION_THRESHOLD;
  }

  /**
   * Find all faces that should receive shadow (top faces + side faces)
   * @returns {Array} Array of {cuboid, face, distance, faceType}
   */
  _findAllShadowFaces() {
    const hits = [];

    // Character position (shadow casts from here straight down)
    const charX = this.character.x;
    const charY = this.character.y;
    const charZ = this.character.z;

    // Query volume straight down from character (expanded for side faces)
    const padding = this.radius * 2;
    const queryBox = {
      minX: charX - padding,
      minY: charY - this.maxDistance,
      minZ: charZ - padding,
      maxX: charX + padding,
      maxY: charY + 0.1,  // Slightly above feet to catch surface we're on
      maxZ: charZ + padding
    };

    // Get candidate cuboids from spatial index
    const candidates = this.collisionSystem.queryCuboids(queryBox);

    // Check each cuboid for top and side faces
    for (const cuboid of candidates) {
      // Skip only if cuboid has no element at all (can't query DOM)
      if (!cuboid.faceElements && !cuboid.element) continue;

      // Check top face
      this._checkTopFace(cuboid, charX, charY, charZ, hits);

      // Check side faces
      this._checkSideFaces(cuboid, charX, charY, charZ, hits);
    }

    // Filter faces accounting for occlusion
    return this._filterWithOcclusion(hits, charX, charY, charZ);
  }

  /**
   * Check if a cuboid's top face receives shadow
   */
  _checkTopFace(cuboid, charX, charY, charZ, hits) {
    // Get face element - check cache first, verify it's connected, then query DOM
    let faceEl = cuboid.faceElements?.['top'];
    // Verify cached element is still in DOM (faces get recreated on camera rotation)
    if (faceEl && !faceEl.isConnected) {
      faceEl = null;
    }
    if (!faceEl && cuboid.element) {
      faceEl = cuboid.element.querySelector('.face.top');
    }
    if (!faceEl) return;

    // Top face Y position
    const topY = cuboid.y + cuboid.height;

    // Check if shadow would land on this face (face is below or at character feet)
    if (topY > charY + 0.01) return;

    // Check if character XZ overlaps with face (with shadow radius padding)
    if (charX + this.radius < cuboid.x || charX - this.radius > cuboid.x + cuboid.width) return;
    if (charZ + this.radius < cuboid.z || charZ - this.radius > cuboid.z + cuboid.depth) return;

    // Distance from character feet to this surface
    const distance = charY - topY;

    if (distance >= 0 && distance < this.maxDistance) {
      hits.push({
        cuboid,
        face: 'top',
        distance,
        faceType: 'horizontal',
        faceY: topY
      });
    }
  }

  /**
   * Check if a cuboid's side faces receive shadow
   */
  _checkSideFaces(cuboid, charX, charY, charZ, hits) {
    const cuboidTop = cuboid.y + cuboid.height;
    const cuboidBottom = cuboid.y;

    // Shadow cylinder goes from charY down to charY - maxDistance
    const shadowTop = charY;
    const shadowBottom = charY - this.maxDistance;

    // Check if cuboid vertically overlaps with shadow cylinder
    if (cuboidTop < shadowBottom || cuboidBottom > shadowTop) return;

    // Helper to get face element (check cache, verify connected, then DOM)
    const getFaceEl = (faceName) => {
      let el = cuboid.faceElements?.[faceName];
      // Verify cached element is still in DOM (faces get recreated on camera rotation)
      if (el && !el.isConnected) {
        el = null;
      }
      if (!el && cuboid.element) {
        el = cuboid.element.querySelector(`.face.${faceName}`);
      }
      return el;
    };

    // Check each side face
    // Back face (at cuboid.z + depth, far Z edge)
    if (getFaceEl('back')) {
      const faceZ = cuboid.z + cuboid.depth;
      // Shadow center is at charZ, check if face edge is within shadow radius
      const distFromCenter = faceZ - charZ;
      if (distFromCenter > -this.radius && distFromCenter < this.radius) {
        // Also check X overlap
        if (charX + this.radius > cuboid.x && charX - this.radius < cuboid.x + cuboid.width) {
          hits.push({
            cuboid,
            face: 'back',
            faceType: 'vertical',
            faceAxis: 'z',
            faceCoord: faceZ,
            distFromCenter: Math.abs(distFromCenter)
          });
        }
      }
    }

    // Front face (at cuboid.z, near Z edge)
    if (getFaceEl('front')) {
      const faceZ = cuboid.z;
      const distFromCenter = charZ - faceZ;
      if (distFromCenter > -this.radius && distFromCenter < this.radius) {
        if (charX + this.radius > cuboid.x && charX - this.radius < cuboid.x + cuboid.width) {
          hits.push({
            cuboid,
            face: 'front',
            faceType: 'vertical',
            faceAxis: 'z',
            faceCoord: faceZ,
            distFromCenter: Math.abs(distFromCenter)
          });
        }
      }
    }

    // Right face (at cuboid.x + width, faces +X direction)
    if (getFaceEl('right')) {
      const faceX = cuboid.x + cuboid.width;
      const distFromCenter = faceX - charX;
      if (distFromCenter > -this.radius && distFromCenter < this.radius) {
        if (charZ + this.radius > cuboid.z && charZ - this.radius < cuboid.z + cuboid.depth) {
          hits.push({
            cuboid,
            face: 'right',
            faceType: 'vertical',
            faceAxis: 'x',
            faceCoord: faceX,
            distFromCenter: Math.abs(distFromCenter)
          });
        }
      }
    }

    // Left face (at cuboid.x, faces -X direction)
    if (getFaceEl('left')) {
      const faceX = cuboid.x;
      const distFromCenter = charX - faceX;
      if (distFromCenter > -this.radius && distFromCenter < this.radius) {
        if (charZ + this.radius > cuboid.z && charZ - this.radius < cuboid.z + cuboid.depth) {
          hits.push({
            cuboid,
            face: 'left',
            faceType: 'vertical',
            faceAxis: 'x',
            faceCoord: faceX,
            distFromCenter: Math.abs(distFromCenter)
          });
        }
      }
    }
  }

  /**
   * Filter faces to account for occlusion by higher surfaces
   * @param {Array} hits - All potential face hits
   * @param {number} charX - Character X position
   * @param {number} charY - Character Y position
   * @param {number} charZ - Character Z position
   * @returns {Array} Filtered hits with occlusion info
   */
  _filterWithOcclusion(hits, charX, charY, charZ) {
    // Separate horizontal and vertical faces
    const horizontalHits = hits.filter(h => h.faceType === 'horizontal');
    const verticalHits = hits.filter(h => h.faceType === 'vertical');

    const result = [];
    const CELL_SIZE = 0.1;
    const coveredCells = new Map(); // cellKey -> highest Y that covers this cell

    if (horizontalHits.length > 0) {
      // Sort horizontal faces by height (highest first = closest to character)
      horizontalHits.sort((a, b) => b.faceY - a.faceY);

      // Get the topmost height level
      const topHeight = horizontalHits[0].faceY;

      for (const hit of horizontalHits) {
        const cuboid = hit.cuboid;

        // Calculate the intersection area of shadow circle with this face
        const overlapMinX = Math.max(cuboid.x, charX - this.radius);
        const overlapMaxX = Math.min(cuboid.x + cuboid.width, charX + this.radius);
        const overlapMinZ = Math.max(cuboid.z, charZ - this.radius);
        const overlapMaxZ = Math.min(cuboid.z + cuboid.depth, charZ + this.radius);

        if (overlapMinX >= overlapMaxX || overlapMinZ >= overlapMaxZ) continue;

        // For faces at the topmost height level, include ALL of them (no cell filtering)
        // This ensures adjacent cuboids at the same height all show the shadow
        if (Math.abs(hit.faceY - topHeight) < 0.05) {
          result.push(hit);
          // Mark cells as covered for lower faces
          for (let x = overlapMinX; x < overlapMaxX; x += CELL_SIZE) {
            for (let z = overlapMinZ; z < overlapMaxZ; z += CELL_SIZE) {
              const cellKey = `${Math.floor(x / CELL_SIZE)},${Math.floor(z / CELL_SIZE)}`;
              coveredCells.set(cellKey, hit.faceY);
            }
          }
        } else {
          // Lower face - only show in cells not covered by higher faces
          let hasUncoveredArea = false;
          for (let x = overlapMinX; x < overlapMaxX; x += CELL_SIZE) {
            for (let z = overlapMinZ; z < overlapMaxZ; z += CELL_SIZE) {
              const cellKey = `${Math.floor(x / CELL_SIZE)},${Math.floor(z / CELL_SIZE)}`;
              if (!coveredCells.has(cellKey)) {
                hasUncoveredArea = true;
                break;
              }
            }
            if (hasUncoveredArea) break;
          }

          if (hasUncoveredArea) {
            result.push(hit);
            for (let x = overlapMinX; x < overlapMaxX; x += CELL_SIZE) {
              for (let z = overlapMinZ; z < overlapMaxZ; z += CELL_SIZE) {
                const cellKey = `${Math.floor(x / CELL_SIZE)},${Math.floor(z / CELL_SIZE)}`;
                if (!coveredCells.has(cellKey)) {
                  coveredCells.set(cellKey, hit.faceY);
                }
              }
            }
          }
        }
      }
    }

    // Process vertical faces - only show if not entirely occluded by horizontal faces
    // The streak is created by the part of shadow extending PAST the face (outside the cuboid)
    for (const hit of verticalHits) {
      const cuboid = hit.cuboid;

      // Calculate the streak width based on distance from shadow center
      const halfWidth = Math.sqrt(Math.max(0, 1 - (hit.distFromCenter / this.radius) ** 2)) * this.radius;

      if (halfWidth <= 0) continue; // No visible streak at this distance

      let checkMinX, checkMaxX, checkMinZ, checkMaxZ;

      // For each face type, check the cells OUTSIDE the face (where shadow extends past)
      // These are the cells that would need to be uncovered for the streak to be visible
      if (hit.face === 'front') {
        // Front face at z = faceCoord, faces -Z direction
        // Check shadow cells in front of the face (z < faceCoord)
        checkMinX = Math.max(cuboid.x, charX - halfWidth);
        checkMaxX = Math.min(cuboid.x + cuboid.width, charX + halfWidth);
        checkMinZ = charZ - this.radius;
        checkMaxZ = hit.faceCoord; // Up to the face position
      } else if (hit.face === 'back') {
        // Back face at z = faceCoord, faces +Z direction
        // Check shadow cells behind the face (z > faceCoord)
        checkMinX = Math.max(cuboid.x, charX - halfWidth);
        checkMaxX = Math.min(cuboid.x + cuboid.width, charX + halfWidth);
        checkMinZ = hit.faceCoord; // From the face position
        checkMaxZ = charZ + this.radius;
      } else if (hit.face === 'left') {
        // Left face at x = faceCoord, faces -X direction
        // Check shadow cells in front of the face (x < faceCoord)
        checkMinX = charX - this.radius;
        checkMaxX = hit.faceCoord; // Up to the face position
        checkMinZ = Math.max(cuboid.z, charZ - halfWidth);
        checkMaxZ = Math.min(cuboid.z + cuboid.depth, charZ + halfWidth);
      } else { // right
        // Right face at x = faceCoord, faces +X direction
        // Check shadow cells behind the face (x > faceCoord)
        checkMinX = hit.faceCoord; // From the face position
        checkMaxX = charX + this.radius;
        checkMinZ = Math.max(cuboid.z, charZ - halfWidth);
        checkMaxZ = Math.min(cuboid.z + cuboid.depth, charZ + halfWidth);
      }

      // Ensure we have a valid check range (handle floating point edge cases)
      if (checkMinX >= checkMaxX || checkMinZ >= checkMaxZ) {
        // Edge case: character is exactly at face boundary, show streak anyway
        result.push(hit);
        continue;
      }

      // Check if the area outside the face has any uncovered cells
      // A cell is "uncovered" for this vertical face if:
      // 1. No horizontal face covers it at all, OR
      // 2. The covering horizontal face is BELOW this vertical face's top
      //    (shadow reaches vertical face before hitting that horizontal surface)
      const verticalTopY = cuboid.y + cuboid.height;
      let hasUncoveredArea = false;
      for (let x = checkMinX; x < checkMaxX && !hasUncoveredArea; x += CELL_SIZE) {
        for (let z = checkMinZ; z < checkMaxZ && !hasUncoveredArea; z += CELL_SIZE) {
          const cellKey = `${Math.floor(x / CELL_SIZE)},${Math.floor(z / CELL_SIZE)}`;
          const coveringY = coveredCells.get(cellKey);
          // Cell is uncovered if: not covered at all, OR covered by surface below vertical face
          if (coveringY === undefined || coveringY < verticalTopY) {
            hasUncoveredArea = true;
          }
        }
      }

      // Only include if there's uncovered area (streak is visible somewhere)
      if (hasUncoveredArea) {
        result.push(hit);
      }
    }

    return result;
  }

  /**
   * Redirect shadow hits to KD tree fragment faces when the original cuboid is split.
   * The collision system returns original cuboids, but when a cuboid is KD-split,
   * the original is hidden and fragments are rendered instead. This redirects each
   * hit to the corresponding fragment face(s) so shadows appear on visible elements.
   * @param {Array} hits - Array of hit objects from _findAllShadowFaces
   * @returns {Array} Resolved hits targeting visible (fragment or original) faces
   */
  _resolveKDFragments(hits) {
    const kdTree = this.collisionSystem.world?.kdTree;
    if (!kdTree || kdTree.splitCuboids.size === 0) {
      return hits; // No KD splits active, use original hits
    }

    const resolved = [];
    for (const hit of hits) {
      const cuboidKey = hit.cuboid.getKey();
      if (kdTree.splitCuboids.has(cuboidKey)) {
        // Cuboid is KD-split — redirect to fragment faces
        const fragments = kdTree.getFragments(cuboidKey);
        if (fragments) {
          for (const frag of fragments) {
            // Only use fragments that have this face visible (not hidden by split)
            if (frag.faceElements[hit.face]) {
              resolved.push({ ...hit, cuboid: frag });
            }
          }
        }
      } else {
        // Not split — use original
        resolved.push(hit);
      }
    }
    return resolved;
  }

  /**
   * Apply shadow overlay to a cuboid face
   * @param {Object} hit - Hit info {cuboid, face, distance, faceType}
   * @param {Object} camera - Camera object
   */
  _applyShadowToFace(hit, camera) {
    const { cuboid, face, faceType } = hit;
    // Get face element - check cache first, verify connected, then query DOM
    let faceEl = cuboid.faceElements?.[face];
    // Verify cached element is still in DOM (faces get recreated on camera rotation)
    if (faceEl && !faceEl.isConnected) {
      faceEl = null;
    }
    if (!faceEl && cuboid.element) {
      faceEl = cuboid.element.querySelector(`.face.${face}`);
    }
    if (!faceEl) return;

    const faceKey = `${cuboid.getKey()}:${face}`;

    // Get or create overlay
    let overlay = this._shadowedFaces.get(faceKey);

    // Check if cached overlay is still valid (face element might have been recreated)
    if (overlay && !faceEl.contains(overlay)) {
      // Face element changed, remove stale reference
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      overlay = null;
      this._shadowedFaces.delete(faceKey);
    }

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'character-shadow-overlay';
      faceEl.appendChild(overlay);
      this._shadowedFaces.set(faceKey, overlay);
    }

    if (faceType === 'horizontal') {
      this._applyHorizontalShadow(hit, overlay, camera);
    } else {
      this._applyVerticalShadow(hit, overlay, camera);
    }
  }

  /**
   * Apply circular shadow to a horizontal (top) face
   * Uses pixel-based positioning for correct rendering when shadow center
   * is outside the face boundaries (adjacent tiled cuboids)
   */
  _applyHorizontalShadow(hit, overlay, camera) {
    const { cuboid, distance } = hit;

    // Calculate shadow position on this face (in pixels)
    const projection = this._projectShadowToTopFace(cuboid, camera);

    // Calculate opacity based on distance
    const opacity = this._calculateOpacity(distance);

    // Apply radial gradient with pixel-based positioning
    // Using 'at Xpx Ypx' allows center to be outside element bounds
    overlay.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      display: block;
      background: radial-gradient(
        ellipse ${projection.radiusXPx}px ${projection.radiusYPx}px at ${projection.centerXPx}px ${projection.centerYPx}px,
        rgba(0,0,0,${opacity}) 0%,
        rgba(0,0,0,${opacity * 0.3}) 70%,
        transparent 100%
      );
      mix-blend-mode: multiply;
      z-index: 999;
    `;
  }

  /**
   * Apply vertical streak shadow to a side face
   */
  _applyVerticalShadow(hit, overlay, camera) {
    const { cuboid, face, distFromCenter } = hit;

    const charX = this.character.x;
    const charY = this.character.y;
    const charZ = this.character.z;

    // Face dimensions in world units
    const cuboidTop = cuboid.y + cuboid.height;
    const cuboidBottom = cuboid.y;

    // Shadow vertical range on this face
    const shadowTop = Math.min(charY, cuboidTop);
    const shadowBottom = Math.max(charY - this.maxDistance, cuboidBottom);

    if (shadowTop <= shadowBottom) {
      overlay.style.display = 'none';
      return;
    }

    // Calculate streak horizontal position and width
    // Width based on circular cross-section at this distance from center
    const halfWidth = Math.sqrt(Math.max(0, 1 - (distFromCenter / this.radius) ** 2)) * this.radius;

    let centerXPercent, widthPercent;

    if (face === 'front') {
      // Front face: X varies across width
      const localX = charX - cuboid.x;
      centerXPercent = (localX / cuboid.width) * 100;
      widthPercent = (halfWidth * 2 / cuboid.width) * 100;
    } else if (face === 'back') {
      // Back face: X varies across width (inverted)
      const localX = charX - cuboid.x;
      centerXPercent = (1 - localX / cuboid.width) * 100;
      widthPercent = (halfWidth * 2 / cuboid.width) * 100;
    } else if (face === 'left') {
      // Left face: Z varies across depth (inverted)
      const localZ = charZ - cuboid.z;
      centerXPercent = (1 - localZ / cuboid.depth) * 100;
      widthPercent = (halfWidth * 2 / cuboid.depth) * 100;
    } else {
      // Right face: Z varies across depth (not inverted)
      const localZ = charZ - cuboid.z;
      centerXPercent = (localZ / cuboid.depth) * 100;
      widthPercent = (halfWidth * 2 / cuboid.depth) * 100;
    }

    // Calculate vertical position (Y is inverted: 0% = top of face = high Y)
    const topPercent = (1 - (shadowTop - cuboid.y) / cuboid.height) * 100;
    const bottomPercent = (1 - (shadowBottom - cuboid.y) / cuboid.height) * 100;
    const heightPercent = bottomPercent - topPercent;

    // Calculate opacity at top and bottom of streak based on actual distance from character feet
    // This ensures continuous fade across stacked cuboids (no banding)
    const distanceToTop = charY - shadowTop;
    const distanceToBottom = charY - shadowBottom;
    const topOpacity = this._calculateOpacity(distanceToTop);
    const bottomOpacity = this._calculateOpacity(distanceToBottom);

    // Edge factor makes streak fainter at the edges of the shadow circle
    const edgeFactor = Math.max(0, 1 - (distFromCenter / this.radius));
    const adjustedTopOpacity = topOpacity * edgeFactor;
    const adjustedBottomOpacity = bottomOpacity * edgeFactor;

    // Position and size the overlay as a vertical streak
    const leftPercent = centerXPercent - widthPercent / 2;

    // Fade-in zone ABOVE the hard edge so shadow eases in before reaching full opacity at feet
    const FADE_IN_DISTANCE = 0.5; // world units
    const fadeInPercent = Math.min((FADE_IN_DISTANCE / cuboid.height) * 100, topPercent);
    const fadeStartPercent = Math.max(0, topPercent - fadeInPercent);

    // Use inset:0 and mask for proper horizontal edge fading
    overlay.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      display: block;
      background: linear-gradient(
        to bottom,
        transparent ${fadeStartPercent}%,
        rgba(0,0,0,${adjustedTopOpacity}) ${topPercent}%,
        rgba(0,0,0,${adjustedBottomOpacity}) ${bottomPercent}%,
        transparent ${bottomPercent}%
      );
      -webkit-mask-image: linear-gradient(
        to right,
        transparent ${leftPercent}%,
        black ${leftPercent + widthPercent * 0.3}%,
        black ${leftPercent + widthPercent * 0.7}%,
        transparent ${leftPercent + widthPercent}%
      );
      mask-image: linear-gradient(
        to right,
        transparent ${leftPercent}%,
        black ${leftPercent + widthPercent * 0.3}%,
        black ${leftPercent + widthPercent * 0.7}%,
        transparent ${leftPercent + widthPercent}%
      );
      mix-blend-mode: multiply;
      z-index: 999;
    `;
  }

  /**
   * Project shadow onto a top face (straight down)
   * Uses pixel-based positioning so shadow center can be outside face boundaries
   * @param {Cuboid} cuboid - Cuboid with the face
   * @param {Object} camera - Camera object
   * @returns {Object} {centerXPx, centerYPx, radiusXPx, radiusYPx}
   */
  _projectShadowToTopFace(cuboid, camera) {
    const charX = this.character.x;
    const charZ = this.character.z;

    // Shadow center in face-local coordinates (world units)
    const localX = charX - cuboid.x;
    const localZ = charZ - cuboid.z;

    // Face dimensions and scaling
    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const cubeSize = cuboid.cubeSize || 60;
    const zoom = DOMUtils.getEffectiveZoom(camera.zoomLevel);
    const scale = cubeSize * zoom;

    // Face dimensions in pixels
    const faceWidthPx = cuboid.width * scale;
    const faceHeightPx = cuboid.depth * scale;

    // Shadow center in pixels (can be negative or exceed face dimensions)
    const centerXPx = (localX / cuboid.width) * faceWidthPx;
    // Invert Z because CSS Y increases downward but face is transformed
    const centerYPx = (1 - localZ / cuboid.depth) * faceHeightPx;

    // Shadow radius in pixels
    const radiusXPx = this.radius * scale;
    const radiusYPx = this.radius * scale;

    return {
      centerXPx,
      centerYPx,
      radiusXPx,
      radiusYPx
    };
  }

  /**
   * Calculate shadow opacity based on distance
   * Linear fade from maxOpacity at distance=0 to 0 at distance=maxDistance
   * @param {number} distance - Distance from character to shadow
   * @returns {number} Opacity (0-1)
   */
  _calculateOpacity(distance) {
    if (distance <= 0) {
      return this.maxOpacity;
    }

    if (distance >= this.maxDistance) {
      return 0;
    }

    // Linear fade from maxOpacity to 0
    const t = distance / this.maxDistance;
    return this.maxOpacity * (1 - t);
  }

  /**
   * Clear all shadow overlays
   */
  clearShadows() {
    for (const overlay of this._shadowedFaces.values()) {
      overlay.style.display = 'none';
    }
    this._activeFaceKeys.clear();
  }

  /**
   * Destroy and clean up
   */
  destroy() {
    for (const overlay of this._shadowedFaces.values()) {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }
    this._shadowedFaces.clear();
    this._activeFaceKeys.clear();
  }
}
